import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiClient } from "@/lib/api";
import { funnelEvent } from "@/lib/attribution";
import { initPixels, pixelTrack } from "@/lib/pixels";

const NAVY = "#132a63";
const GREEN = "#8CE313";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function Gracias() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading"); // loading | ok | pending | error
  const [data, setData] = useState(null);
  const [upsellDone, setUpsellDone] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    initPixels();
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await apiClient.get(`/funnel/status/${sessionId}`);
        if (data.payment_status === "approved") {
          setData(data); setState("ok");
          if (!fired.current) {
            fired.current = true;
            funnelEvent("membership_activated", { amount: data.precio });
            pixelTrack("purchase", { value: data.precio, eventId: sessionId });
          }
          return;
        }
      } catch { /* retry */ }
      if (tries < 8) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const wrap = { minHeight: "100vh", background: NAVY, color: "white", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Poppins',system-ui,sans-serif", textAlign: "center" };

  if (state === "loading") {
    return <div style={wrap}><div><i className="fas fa-spinner fa-spin" style={{ fontSize: 40, color: GREEN }} /><p style={{ marginTop: 16 }}>Confirmando tu pago…</p></div></div>;
  }
  if (state === "pending") {
    return <div style={wrap}><div><i className="fas fa-clock" style={{ fontSize: 40, color: GREEN }} /><p style={{ marginTop: 16, maxWidth: 400 }}>Tu pago se está procesando. En cuanto se confirme recibirás tu folio por email y WhatsApp.</p><Link to="/" style={{ color: GREEN }}>Volver al inicio</Link></div></div>;
  }
  if (state === "error") {
    return <div style={wrap}><div><i className="fas fa-triangle-exclamation" style={{ fontSize: 40, color: GREEN }} /><p style={{ marginTop: 16 }}>No pudimos encontrar tu pago.</p><Link to="/activar" style={{ color: GREEN }}>Intentar de nuevo</Link></div></div>;
  }

  return (
    <div style={wrap}>
      <Helmet><title>¡Tu membresía está activa! — Mi Ángel Asís</title></Helmet>
      <div style={{ background: "white", color: "#1f2937", borderRadius: 22, padding: "34px 26px", maxWidth: 460, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} data-testid="confirmacion-card">
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#EAF6D6", color: "#5BA628", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34, margin: "0 auto 16px" }}>
          <i className="fas fa-check" />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: NAVY, margin: 0 }}>Tu membresía ya está activa</h1>
        <p style={{ color: "#6b7280", marginTop: 6 }}>¡Gracias por proteger a tu familia, {data?.nombre || ""}!</p>

        <div style={{ background: "#F3F7FF", borderRadius: 14, padding: 18, margin: "18px 0", textAlign: "left" }}>
          <Row label="Folio" value={data?.folio} testid="conf-folio" strong />
          <Row label="Inicio" value={fmtDate(data?.fecha_inicio)} />
          <Row label="Vigencia hasta" value={fmtDate(data?.fecha_vencimiento)} testid="conf-vigencia" />
          {upsellDone && (
            <div data-testid="upsell-badge" style={{ marginTop: 8, background: "#EAF6D6", color: "#3f7a12", borderRadius: 8, padding: "8px 10px", fontWeight: 700, fontSize: 13, textAlign: "center" }}>
              🎉 Plan Anual activado — cubierto los próximos 12 meses
            </div>
          )}
        </div>

        {data?.upsell?.available && !upsellDone && (
          <UpsellOffer sessionId={sessionId} upsell={data.upsell}
            onDone={(venc) => { setUpsellDone(true); setData((d) => ({ ...d, fecha_vencimiento: venc })); }} />
        )}

        <div style={{ textAlign: "left", fontSize: 14, color: "#374151", marginBottom: 18 }}>
          <div style={{ fontWeight: 700, color: NAVY, marginBottom: 6 }}>Ya puedes usar:</div>
          <div><i className="fas fa-check" style={{ color: "#5BA628", marginRight: 8 }} />Asistencia médica, psicológica y nutricional</div>
          <div><i className="fas fa-check" style={{ color: "#5BA628", marginRight: 8 }} />Orientación legal y apoyo familiar</div>
        </div>

        <a href="/cuenta/login" data-testid="conf-cuenta"
          style={{ display: "block", background: NAVY, color: "white", padding: "14px", borderRadius: 999, fontWeight: 800, textDecoration: "none", marginBottom: 10 }}>
          Ir a mi cuenta
        </a>
        <a href="https://wa.me/525500000000" target="_blank" rel="noreferrer" data-testid="conf-whatsapp"
          style={{ display: "block", background: "#25D366", color: "white", padding: "14px", borderRadius: 999, fontWeight: 800, textDecoration: "none" }}>
          <i className="fab fa-whatsapp" /> Contactar asistencia
        </a>
      </div>
    </div>
  );
}

function UpsellOffer({ sessionId, upsell, onDone }) {
  const [status, setStatus] = useState("idle"); // idle | working | error | hidden
  const [errMsg, setErrMsg] = useState("");
  if (status === "hidden") return null;

  const accept = async () => {
    setStatus("working");
    try {
      const { data } = await apiClient.post("/funnel/upsell", { session_id: sessionId });
      onDone(data.fecha_vencimiento);
    } catch (err) {
      setErrMsg(err.response?.data?.detail || "No pudimos procesar el cargo. Intenta más tarde.");
      setStatus("error");
    }
  };

  return (
    <div data-testid="upsell-offer" style={{ border: `2px solid ${GREEN}`, background: "#F7FCEB", borderRadius: 16, padding: "18px 16px", marginBottom: 18, textAlign: "left" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ background: NAVY, color: GREEN, fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 999, letterSpacing: 0.5 }}>OFERTA ÚNICA · SOLO AQUÍ</span>
      </div>
      <div style={{ fontWeight: 800, color: NAVY, fontSize: 17, lineHeight: 1.3 }}>
        Cámbiate al plan Anual con 30% OFF
      </div>
      <p style={{ fontSize: 13.5, color: "#4b5563", margin: "6px 0 10px" }}>
        Quedas protegido 12 meses completos por <strong>{money(upsell.total_anual)}</strong>{" "}
        <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>{money(upsell.precio_regular)}</span>.
        Solo pagas la diferencia con tu misma tarjeta, sin volver a teclear datos.
      </p>
      {status === "error" && (
        <div data-testid="upsell-error" style={{ background: "#FEF2F2", color: "#b91c1c", borderRadius: 8, padding: "8px 10px", fontSize: 13, marginBottom: 10 }}>{errMsg}</div>
      )}
      <button onClick={accept} disabled={status === "working"} data-testid="upsell-accept"
        style={{ width: "100%", background: "#5BA628", color: "white", border: "none", padding: "13px", borderRadius: 999, fontWeight: 800, fontSize: 15, cursor: "pointer", fontFamily: "inherit", opacity: status === "working" ? 0.7 : 1 }}>
        {status === "working" ? <><i className="fas fa-spinner fa-spin" /> Procesando…</> : <>Sí, quiero el año completo — pagar {money(upsell.pagar_ahora)}</>}
      </button>
      <button onClick={() => setStatus("hidden")} data-testid="upsell-decline"
        style={{ width: "100%", background: "none", border: "none", color: "#6b7280", fontSize: 13, marginTop: 8, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
        No gracias, me quedo con el plan mensual
      </button>
    </div>
  );
}

function Row({ label, value, strong, testid }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
      <span style={{ color: "#6b7280", fontSize: 14 }}>{label}</span>
      <span data-testid={testid} style={{ fontWeight: strong ? 800 : 600, color: "#132a63", fontSize: strong ? 18 : 14 }}>{value || "—"}</span>
    </div>
  );
}
